import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UsersThree, ShieldCheck, ShieldSlash, Crown, Key, Trash, UserPlus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import SubscriptionTokens from "./SubscriptionTokens";
import { logError } from "@/lib/log";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState("");
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDays, setExtendDays] = useState(1);
  const [extending, setExtending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/admin/stats")]);
      setUsers(u.data);
      setStats(s.data);
      setLoadError("");
    } catch (e) { logError("Gagal memuat data admin:", e); setLoadError("Data user gagal dimuat. Coba muat ulang."); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const update = async (id, patch, msg) => {
    try {
      await api.put(`/admin/users/${id}`, patch);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal update");
    }
  };

  const resetPw = async (u) => {
    const np = window.prompt(`Set password baru untuk ${u.email} (min 6 karakter):`);
    if (np === null) return;
    if (np.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    try {
      await api.post(`/admin/users/${u.id}/reset-password`, { new_password: np });
      toast.success(`Password ${u.email} berhasil direset`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal reset password");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Password minimal 8 karakter");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/users", { email: newEmail.trim(), password: newPassword, role: "user", plan: "free" });
      toast.success(`Akun pelanggan ${newEmail.trim()} dibuat`);
      setNewEmail("");
      setNewPassword("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat user");
    } finally {
      setCreating(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Hapus permanen user ${u.email}? Semua data terkait (akun Telegram, config, log) ikut terhapus.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`User ${u.email} dihapus`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal hapus user");
    }
  };

  const filteredUsers = users.filter((u) => u.email.toLowerCase().includes(search.trim().toLowerCase()));

  const extendSubscription = async (event) => {
    event.preventDefault();
    const days = Number(extendDays);
    if (!extendTarget || !Number.isInteger(days) || days < 1 || days > 365) {
      toast.error("Masukkan jumlah hari antara 1 dan 365");
      return;
    }
    setExtending(true);
    try {
      await api.post(`/admin/users/${extendTarget.id}/extend`, { days });
      toast.success(`Masa aktif ${extendTarget.email} ditambah ${days} hari`);
      setExtendTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah masa aktif");
    } finally {
      setExtending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-pink-300 mb-2">Pengelolaan</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold flex items-center gap-3">
          <Crown size={32} weight="duotone" className="text-pink-300" />
          Kelola pelanggan
        </h1>
        <p className="mt-2 text-sm text-slate-400">Cari pelanggan, perbarui masa aktif, dan kelola akses dari halaman ini.</p>
      </div>

      <nav className="flex flex-wrap gap-2 text-sm" aria-label="Bagian pengelolaan">
        <a href="#daftar-pelanggan" className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-slate-200 hover:bg-white/10">Daftar pelanggan</a>
        <a href="#buat-pelanggan" className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-slate-200 hover:bg-white/10">Buat akun</a>
        <a href="#token-langganan" className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-slate-200 hover:bg-white/10">Token langganan</a>
      </nav>

      {loadError && <p role="alert" className="text-amber-300">{loadError} <button onClick={load} className="underline">Coba lagi</button></p>}
      {stats && (
        <div className="grid md:grid-cols-4 gap-4">
          <Stat label="Total User" value={stats.total_users} testid="stat-total" />
          <Stat label="User Aktif" value={stats.active_users} testid="stat-active" />
          <Stat label="Bot Berjalan" value={stats.running_bots} testid="stat-running" />
          <Stat
            label="Paket Populer"
            value={
              Object.entries(stats.plans || {})
                .sort((a, b) => b[1] - a[1])[0]?.[0] || "-"
            }
            testid="stat-plan"
          />
        </div>
      )}

      <div id="buat-pelanggan" className="border border-white/15 bg-[#0F0F16] rounded-xl p-5" data-testid="create-user-card">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus size={18} className="text-pink-400" />
          <span className="font-heading font-bold">Buat Akun Pelanggan</span>
          <span className="text-xs text-slate-500 ml-auto">Akun baru belum berlangganan. Aktifkan dengan token Pro atau Elite.</span>
        </div>
        <form onSubmit={createUser} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
          <Input
            type="email"
            aria-label="Email pelanggan"
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Email pelanggan"
            data-testid="new-user-email"
            className="bg-[#05050A] border-white/10 focus:border-pink-500 h-10"
          />
          <Input
            type="password"
            autoComplete="new-password"
            aria-label="Password pelanggan"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="password (min 8 karakter)"
            data-testid="new-user-password"
            className="bg-[#05050A] border-white/10 focus:border-pink-500 h-10 font-mono"
          />
          <Button
            type="submit"
            disabled={creating}
            data-testid="create-user-submit"
            className="bg-pink-500 hover:bg-pink-600 text-black font-bold h-10 rounded-md px-6"
          >
            {creating ? "Membuat..." : "Buat"}
          </Button>
        </form>
      </div>

      <div id="daftar-pelanggan" className="border border-white/15 bg-[#0F0F16] rounded-xl overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center gap-2">
          <UsersThree size={18} className="text-pink-300" />
          <span className="font-heading font-bold">Daftar pelanggan</span>
          <span className="text-xs text-slate-400 ml-auto">{filteredUsers.length} dari {users.length} akun</span>
        </div>
        <div className="px-4 py-3"><Input aria-label="Cari user" placeholder="Cari email pelanggan…" value={search} onChange={e=>setSearch(e.target.value)} className="max-w-sm bg-black/30 border-white/10" /></div>
        <div data-testid="admin-users-table" className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-slate-400">Email</TableHead>
              <TableHead className="text-slate-400">Role</TableHead>
              <TableHead className="text-slate-400">Plan</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((u) => (
              <TableRow key={u.id} className="border-white/5 hover:bg-white/[0.02]" data-testid="user-row">
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell>
                  <Select
                    value={u.role}
                    onValueChange={(v) => update(u.id, { role: v }, "Role diupdate")}
                  >
                    <SelectTrigger className="h-8 bg-[#05050A] border-white/10 w-28 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F16] border-white/10">
                      <SelectItem value="user">user</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${u.role==='admin'?'bg-yellow-500/10 text-yellow-300':'bg-cyan-400/10 text-cyan-300'}`}>{u.role==='admin'?'Admin · 100 Telegram':u.plan==='elite'?'Elite · 3 Telegram':u.plan==='pro'?'Pro · 1 Telegram':'Belum aktif'}</span>
                  <p className="text-xs text-slate-500 mt-2">{u.subscription_expires_at ? `Sampai ${new Date(u.subscription_expires_at).toLocaleString('id-ID')}` : u.role==='admin'?'Akses admin':u.subscription_required?'Menunggu token':'Akun lama'}</p>
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-mono ${
                      u.is_active
                        ? "bg-green-500/10 text-green-400"
                        : "bg-red-500/10 text-red-400"
                    }`}
                  >
                    {u.is_active ? "aktif" : "disabled"}
                  </span>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {u.role !== "admin" && u.subscription_expires_at && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setExtendDays(1); setExtendTarget(u); }}
                      className="h-8 rounded-md text-xs mr-2 border-pink-500/40 text-pink-300 hover:bg-pink-500/10"
                      data-testid="extend-subscription"
                    >
                      + Durasi
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resetPw(u)}
                    className="h-8 rounded-md text-xs mr-2 border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    data-testid="reset-password"
                    title="Reset password user"
                    aria-label={`Reset password ${u.email}`}
                  >
                    <Key size={14} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      update(
                        u.id,
                        { is_active: !u.is_active },
                        u.is_active ? "User dinonaktifkan" : "User diaktifkan"
                      )
                    }
                    className={`h-8 rounded-md text-xs ${
                      u.is_active
                        ? "border-red-500/30 text-red-400 hover:bg-red-500/10"
                        : "border-green-500/30 text-green-400 hover:bg-green-500/10"
                    }`}
                    data-testid="toggle-active"
                    aria-label={`${u.is_active ? "Nonaktifkan" : "Aktifkan"} ${u.email}`}
                  >
                    {u.is_active ? <ShieldSlash size={14} /> : <ShieldCheck size={14} />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteUser(u)}
                    className="h-8 rounded-md text-xs ml-2 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid="delete-user"
                    title="Hapus user permanen"
                    aria-label={`Hapus ${u.email}`}
                  >
                    <Trash size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-400">Tidak ada pelanggan yang cocok.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      <div id="token-langganan"><SubscriptionTokens /></div>

      <Dialog open={Boolean(extendTarget)} onOpenChange={(open) => { if (!open && !extending) setExtendTarget(null); }}>
        <DialogContent className="max-w-md border-white/15 bg-[#172438] text-slate-100">
          <form onSubmit={extendSubscription} className="space-y-5">
            <DialogHeader>
              <DialogTitle>Tambah masa aktif</DialogTitle>
              <DialogDescription className="text-slate-300">
                {extendTarget?.email}. Paket tetap sama; hari ditambahkan dari tanggal berakhir saat ini.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label htmlFor="extend-days" className="text-sm font-medium">Jumlah hari</label>
              <Input id="extend-days" type="number" min="1" max="365" step="1" value={extendDays}
                onChange={(event) => setExtendDays(event.target.value)} className="bg-[#101c2d] border-white/20" />
              <p className="text-xs text-slate-400">
                Berakhir saat ini: {extendTarget?.subscription_expires_at
                  ? new Date(extendTarget.subscription_expires_at).toLocaleString("id-ID") : "—"}
              </p>
            </div>
            <DialogFooter className="gap-2 sm:space-x-0">
              <Button type="button" variant="outline" disabled={extending} onClick={() => setExtendTarget(null)}>Batal</Button>
              <Button type="submit" disabled={extending} className="bg-pink-500 text-white hover:bg-pink-600">
                {extending ? "Menyimpan..." : "Tambah durasi"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-white/10 bg-[#0F0F16] rounded-lg p-5" data-testid={testid}>
      <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">{label}</div>
      <div className="font-heading text-3xl font-bold">{value}</div>
    </div>
  );
}
